// Login screen for the read-only Continuum admin dashboard.
// Stores the admin key in sessionStorage (not localStorage — auto-cleared on tab close).

import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Lock, AlertTriangle, Loader2 } from "lucide-react";
import { getAdminKey, setAdminKey, verifyAdminKey } from "@/lib/platformApi";
import ContinuumBand from "@/components/ContinuumBand";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [key, setKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  if (getAdminKey()) {
    return <Navigate to="/app" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      await verifyAdminKey(key);
      setAdminKey(key);
      navigate("/app", { replace: true });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || "Invalid key");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="admin-login" className="min-h-[70vh] flex items-center bg-[#0A1224] text-white relative">
      <ContinuumBand className="absolute top-0 left-0 right-0 h-1" />
      <div className="max-w-md w-full mx-auto px-6 py-20">
        <div className="text-xs font-black uppercase tracking-[0.2em] text-slate-400 mb-4">
          Continuum · Internal dashboard
        </div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-3">Sign in.</h1>
        <p className="text-slate-300 leading-relaxed">
          Read-only access to intake submissions and the demo tenant evidence base.
          Requires the team admin key (rotated per release).
        </p>

        <form onSubmit={onSubmit} className="mt-10 space-y-5">
          <div>
            <label htmlFor="admin-key" className="block text-xs font-black uppercase tracking-[0.18em] text-slate-400 mb-2">
              Admin key
            </label>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                id="admin-key"
                data-testid="admin-login-key-input"
                type="password"
                required
                value={key}
                onChange={(e) => setKey(e.target.value)}
                className="w-full bg-white/5 border border-white/20 pl-10 pr-4 py-3 text-white placeholder-white/40 focus:border-[#FF8A00] focus:outline-none transition-colors"
                placeholder="paste team admin key"
                autoFocus
              />
            </div>
          </div>
          {err && (
            <div className="bg-amber-500/10 border border-amber-500 px-4 py-3 flex items-start gap-3 text-sm" data-testid="admin-login-error">
              <AlertTriangle className="text-amber-400 flex-shrink-0 mt-0.5" size={16} />
              <span className="text-amber-100">{err}</span>
            </div>
          )}
          <button
            type="submit"
            disabled={loading || !key}
            data-testid="admin-login-submit"
            className="bg-[#FF8A00] text-[#050A15] font-bold px-6 py-3.5 hover:bg-[#FFA833] disabled:opacity-60 w-full inline-flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : <Lock size={16} />}
            Sign in
          </button>
        </form>

        <p className="text-xs text-slate-500 mt-8 leading-relaxed">
          Session-scoped — your key is held in <span className="font-mono">sessionStorage</span> and
          cleared when you close the tab. There is no email/password recovery for this
          dashboard; rotate the admin key on the server to revoke access.
        </p>
      </div>
    </section>
  );
}
